import { AppProductsSlideover } from '#components'
import { z } from '#netzo/shared/utils/zod'
import type { DropdownItem } from '#ui/types'
import type { ComponentProps } from 'vue-component-type-helpers'

// types:

type AppProductsSlideoverProps = ComponentProps<typeof AppProductsSlideover>

export type ProductsUtils = ReturnType<typeof useProductsUtils>

export const useProducts = () => {
  const {
    onSubmitCreate,
    onSubmitUpdate,
    onSubmitDelete,
    onSubmitUpdateMultiple,
    slideover,
    modal,
    slideoverOpenInformation,
    slideoverOpenFilePreview,
    slideoverOpenFilePreviewPdf,
    modalOpenDelete,
    modalOpenShareFile,
  } = useRows<Product>('products')

  const slideoverOpenCreate = (props: AppProductsSlideoverProps) => {
    return new Promise<Product>((resolve, reject) => {
      const data = getDefaultProduct(props.data)
      slideover.open(AppProductsSlideover, {
        title: 'Crear producto',
        ...props,
        data,
        onSubmit: props?.onSubmit ?? (async (event) => {
          try {
            const result = await onSubmitCreate(event.data)
            resolve(result)
          }
          catch (error) {
            reject(error)
          }
          finally {
            slideover.close() // ensures slideover is always closed
          }
        }),
        action: 'create',
        openNestedCallback: slideoverOpenCreate,
        navigateToSubpage: true,
      })
    })
  }

  const slideoverOpenEdit = (props: AppProductsSlideoverProps) => {
    return new Promise<Product>((resolve, reject) => {
      const data = getDefaultProduct(props.data)
      slideover.open(AppProductsSlideover, {
        title: 'Editar producto',
        ...props,
        data,
        disabledFields: ['type'],
        onSubmit: props?.onSubmit ?? (async (event) => {
          try {
            const result = await onSubmitUpdate(event.data)
            resolve(result)
          }
          catch (error) {
            reject(error)
          }
          finally {
            slideover.close() // ensures slideover is always closed
          }
        }),
        action: 'edit',
        openNestedCallback: slideoverOpenEdit,
      })
    })
  }

  const schema = z.object({
    type: z.string(),
    name: z.string(),
    description: z.string(),
    fiscalData: fiscalDataSchema,
    categoryId: z.string(),
  })

  // actions:

  const getDropdownItems = (row: Product, refresh: () => Promise<void>, utils: ProductsUtils, dropdownItemsAction: DropdownItem[] = []): DropdownItem[][] => [
    [
      getDropdownItem('editOrView', 'products', row, refresh, utils),
    ],
    [
      getDropdownItem('copyId', 'products', row, refresh, utils),
    ],
    [
      getDropdownItem('notes', 'products', row, refresh, utils),
      getDropdownItem('files', 'products', row, refresh, utils),
    ],
    [
      {
        label: 'Crear item',
        icon: ICONS.productitems,
        click: () => {
          const productitems$ = useProductitems()
          productitems$.slideoverOpenCreate({
            data: { productId: row.id },
            disabledFields: ['productId'],
          }).then(refresh)
        },
      },
      ...dropdownItemsAction,
    ],
    [
      getDropdownItem('info', 'products', row, refresh, utils),
      getDropdownItem('immutable', 'products', row, refresh, utils),
    ],
    [
      getDropdownItem('archive', 'products', row, refresh, utils),
      getDropdownItem('delete', 'products', row, refresh, utils),
    ],
  ].filter(items => items?.length)

  return {
    onSubmitCreate,
    onSubmitUpdate,
    onSubmitDelete,
    onSubmitUpdateMultiple,
    slideover,
    modal,
    slideoverOpenInformation,
    slideoverOpenFilePreview,
    slideoverOpenFilePreviewPdf,
    modalOpenDelete,
    modalOpenShareFile,
    slideoverOpenCreate,
    slideoverOpenEdit,
    schema,
    getDropdownItems,
  }
}

export const useProductsUtils = ({ $suppliers, $applications, $categories }: {
  $suppliers?: Awaited<ReturnType<typeof useFetch<Supplier[]>>>
  $applications?: Awaited<ReturnType<typeof useFetch<Application[]>>>
  $categories?: Awaited<ReturnType<typeof useFetch<Category[]>>>
} = {}) => {
  const options = {
    ...optionsProducts,
    applicationId: computed(() => $applications?.data.value.map(toOption)),
    categoryId: computed(() => $categories?.data.value.map(toOption)),
    supplierIdManufacturer: computed(() => $suppliers?.data.value.map(toOption)),
    supplierId: computed(() => $suppliers?.data.value.map(toOption)),
  }

  const maps = {
    type: toMapByKey(optionsProducts.type, 'value'),
    fiscalData: {
      satUnitKey: toMapByKey(optionsSAT.productUnitId, 'value'),
    },
    applicationId: computed(() => toMapByKey($applications?.data.value, 'id')),
    categoryId: computed(() => toMapByKey($categories?.data.value, 'id')),
    supplierIdManufacturer: computed(() => toMapByKey($suppliers?.data.value, 'id')),
    supplierId: computed(() => toMapByKey($suppliers?.data.value, 'id')),
  }

  return { options, maps }
}
